import React, { useCallback, useMemo, useState } from 'react'
import { Button, Grid, Stack, TextField } from '@mui/material'
import { ReactComponent as PreviewIcon } from '../../../common/icons/play_icon.svg'
import { Form, Field } from 'react-final-form'
import CreatePageWrapper from './CreatePageWrapper'
import BackToButton from '../../../common/components/BackToButton'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import ModulesManager from './ModulesManager'
import ModulesPreview from './ModulesPreview'
import {
  useAddCustomModuleMutation,
  useGetCustomModuleQuery,
  useGetModulePreviewQuery,
  useUpdateCustomModuleMutation,
  useUpdateInternalModuleMutation
} from '../../../api/trainingTopics'
import useMutation from '../../../hooks/useMutation'
import LoadingIndicator from '../../../common/components/LoadingIndicator'
import DeleteDialog from '../../../common/components/DeleteDialog'

const validate = values => {
  const errors = {}
  if (!values.name?.trim()) {
    errors.name = 'Module name is required'
  }

  const hasValidStep = values.steps?.some(step => step.type)
  if (!hasValidStep) {
    errors._error = 'At least one step with selected type is required'
  }

  return errors
}

export default function CreateNewModulePage() {
  const navigate = useNavigate()
  const { state } = useLocation()
  const { topicId, moduleId } = useParams()
  const isEditMode = !!moduleId
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [isBackDialogOpen, setIsBackDialogOpen] = useState(false)

  const { data: existingModule, isLoading: isLoadingModule } = useGetCustomModuleQuery(
    { topicId, moduleId, language: 'en' },
    { skip: !isEditMode || state?.type !== 'CUSTOM' }
  )
  const { data: existingInternalModule, isLoading: isLoadingInternalModule } = useGetModulePreviewQuery(
    { topicId, moduleId, language: state?.language || 'en' },
    { skip: !isEditMode || state?.type !== 'INTERNAL' }
  )

  const { submit: handleCreate } = useMutation(useAddCustomModuleMutation, {
    successMessage: 'Module successfully created',
    onSuccess: () => navigate(`/microtraining`, { state: { activeTab: 1 } })
  })

  const { submit: handleUpdateCustom } = useMutation(useUpdateCustomModuleMutation, {
    successMessage: 'Module successfully updated',
    onSuccess: () => navigate(`/microtraining`, { state: { activeTab: 1 } })
  })

  const { submit: handleUpdateInternal } = useMutation(useUpdateInternalModuleMutation, {
    successMessage: 'Module successfully updated',
    onSuccess: () => navigate(`/microtraining`, { state: { activeTab: 1 } })
  })

  const initialValues = useMemo(() => {
    if (isEditMode && existingModule) {
      return existingModule
    }
    if (isEditMode && existingInternalModule) {
      return existingInternalModule
    }
    return {
      name: '',
      theme: 'THEME_1',
      steps: []
    }
  }, [existingModule, existingInternalModule, isEditMode])

  const onSubmit = useCallback(
    values => {
      const filteredSteps = values.steps?.filter(step => step.type) || []
      const dataToSubmit = {
        ...values,
        steps: filteredSteps
      }

      if (isEditMode && state?.type === 'CUSTOM') {
        handleUpdateCustom({ topicId, moduleId, ...dataToSubmit })
        return
      }

      if (isEditMode && state?.type === 'INTERNAL') {
        handleUpdateInternal({ topicId, moduleId, language: state?.language || 'en', ...dataToSubmit })
        return
      }
      handleCreate({ topicId, ...dataToSubmit })
    },
    [isEditMode, handleUpdateInternal, state?.type, topicId, moduleId, handleCreate, handleUpdateCustom]
  )

  function handleBack(form) {
    const { pristine, values, initialValues } = form.getState()
    const themeChanged = values.theme !== initialValues.theme
    if (!pristine || themeChanged) {
      setIsBackDialogOpen(true)
    } else {
      navigate(`/microtraining`, { state: { activeTab: 1 } })
    }
  }

  function handleConfirmBack() {
    setIsBackDialogOpen(false)
    navigate(`/microtraining`, { state: { activeTab: 1 } })
  }

  function handleOpenPreview() {
    setIsPreviewOpen(true)
  }

  function handleClosePreview() {
    setIsPreviewOpen(false)
  }

  if (isLoadingModule || isLoadingInternalModule) {
    return <LoadingIndicator />
  }

  return (
    <CreatePageWrapper>
      <Form
        initialValues={initialValues}
        onSubmit={onSubmit}
        validate={validate}
        render={({ handleSubmit, values, submitting, pristine, invalid, form }) => (
          <form onSubmit={handleSubmit} style={styles.mainStack}>
            <Stack rowGap={2} width={'100%'} sx={styles.mainStack}>
              <Grid>
                <BackToButton onClick={() => handleBack(form)} name={'Back to Custom Topic'} />
              </Grid>
              <Grid container width={'100%'} alignItems={'center'} justifyContent={'space-between'}>
                <Grid item xs={6}>
                  <Field name='name'>
                    {({ input, meta }) => (
                      <TextField
                        {...input}
                        fullWidth
                        variant='outlined'
                        placeholder='Enter module name'
                        error={meta.touched && meta.error}
                        helperText={meta.touched && meta.error}
                        sx={styles.moduleNameInput}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item>
                  <Stack direction='row' spacing={2}>
                    <Button
                      variant='outlined'
                      startIcon={<PreviewIcon />}
                      onClick={handleOpenPreview}
                      sx={styles.previewButton}
                      disabled={!values.steps?.length || !values.steps[0]?.type}
                    >
                      Preview
                    </Button>
                    <Button
                      type='submit'
                      variant='contained'
                      sx={styles.saveButton}
                      disabled={
                        submitting ||
                        (pristine && values.theme === initialValues.theme) ||
                        invalid ||
                        !values.steps?.some(step => step.type)
                      }
                    >
                      Save
                    </Button>
                  </Stack>
                </Grid>
              </Grid>

              <Field name='steps'>
                {() => (
                  <ModulesManager
                    theme={values.theme}
                    onModulesChange={modules => {
                      form.change('steps', modules)
                    }}
                    handleThemeChange={theme => {
                      form.change('theme', theme)
                    }}
                  />
                )}
              </Field>

              <ModulesPreview
                open={isPreviewOpen}
                onClose={handleClosePreview}
                modules={values.steps}
                theme={values.theme}
              />

              {isBackDialogOpen && (
                <DeleteDialog
                  open={isBackDialogOpen}
                  onClose={() => setIsBackDialogOpen(false)}
                  onAction={handleConfirmBack}
                  mainAction='Discard changes'
                  str='You have unsaved changes'
                  description='You have unsaved changes. If you exit now, all modifications will be discarded.'
                />
              )}
            </Stack>
          </form>
        )}
      />
    </CreatePageWrapper>
  )
}

const styles = {
  mainStack: {
    width: '100%',
    maxWidth: 1150,
    height: 'calc(100vh - 40px)'
  },
  moduleNameInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '14px',
      backgroundColor: 'rgba(255, 255, 255, 0.9)'
    }
  },

  previewButton: {
    width: '135px',
    height: '40px',
    borderRadius: '12px',
    textTransform: 'none',
    fontSize: '16px',
    fontWeight: 400,
    fontFamily: 'Plus Jakarta Sans',
    backgroundColor: '#fff',
    border: '1px solid #E7E7EE',
    color: '#6123FF',
    '&:hover': {
      border: 'none',
      backgroundColor: '#F0E7F9'
    }
  },
  saveButton: {
    width: '120px',
    height: '40px',
    borderRadius: '12px',
    textTransform: 'none',
    fontSize: '16px',
    fontWeight: 400,
    fontFamily: 'Plus Jakarta Sans',
    color: '#fff',
    backgroundColor: '#6123FF',
    boxShadow: 'none',
    '&:hover': {
      border: 'none',
      color: '#6123FF',
      backgroundColor: '#F0E7F9'
    }
  }
}
